import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { ptBr } from "./locales/pt-br";
import { enUs } from "./locales/en-us";
import { esEs } from "./locales/es-es";

/**
 * Inicializa o i18n com detecção automática de idioma
 * Mapeia os idiomas detectados para os idiomas suportados
 */
const languageDetector = new LanguageDetector();

// Mapeamento de idiomas detectados para os idiomas suportados
const supportedLanguages: { [key: string]: string } = {
  "en": "en-US",
  "en-US": "en-US",
  "en-GB": "en-US",
  "en-AU": "en-US",
  "pt": "pt-BR",
  "pt-BR": "pt-BR",
  "pt-PT": "pt-BR",
  "es": "es-ES",
  "es-ES": "es-ES",
  "es-MX": "es-ES",
  "es-AR": "es-ES",
};

/**
 * Hook para normalizar o idioma detectado
 */
const normalizeLanguage = (lng: string | string[]): string => {
  if (Array.isArray(lng)) {
    lng = lng[0];
  }
  return supportedLanguages[lng] || "pt-BR";
};

export default async function initI18n() {
  return new Promise((resolve) => {
    i18n
      .use(languageDetector)
      .init(
        {
          resources: {
            "pt-BR": ptBr,
            "en-US": enUs,
            "es-ES": esEs,
          },
          fallbackLng: "pt-BR",
          ns: ["translation"],
          defaultNS: "translation",
          detection: {
            order: ["localStorage", "navigator", "htmlTag"],
            caches: ["localStorage"],
            lookupLocalStorage: "i18nextLng",
            lookupFromPathIndex: 0,
            lookupFromSubdomainIndex: 0,
          },
          interpolation: {
            escapeValue: false, // Stencil já faz sanitização
          },
        },
        (err: any) => {
          if (err) {
            console.error("Erro ao inicializar i18n:", err);
          } else {
            console.log("✅ i18n inicializado com sucesso. Idioma:", i18n.language);
          }
          resolve(undefined);
        }
      );

    // Aplicar normalização de linguagem após inicializar
    const currentLanguage = normalizeLanguage(i18n.language);
    if (currentLanguage !== i18n.language) {
      i18n.changeLanguage(currentLanguage);
    }
  });
}


/**
 * Função principal para tradução - USE ISSO NOS COMPONENTES!
 * Retorna uma função de tradução pronta para usar
 * 
 * @example
 * import { t } from '../../i18n';
 * 
 * // Usar diretamente:
 * <h1>{t('sidebar.header.title')}</h1>
 * 
 * // Ou com lógica:
 * const title = t('sidebar.header.title');
 * console.log(title);
 */
export function t(key: string, defaultValue?: string): string {
  const translation = i18n.t(key);
  
  // Se retornar a própria chave, significa que não foi encontrada
  if (translation === key && defaultValue) {
    console.warn(`[i18n] Tradução não encontrada: ${key}, usando fallback: ${defaultValue}`);
    return defaultValue;
  }
  
  return translation;
}

/**
 * Obtém a instância completa do i18n para operações avançadas
 * Usar apenas quando precisar de changeLanguage, etc
 * 
 * @example
 * import { geti18nInstance } from '../../i18n';
 * const i18nInstance = geti18n();
 * await i18nInstance.changeLanguage('en-US');
 */
export function geti18nInstance() {
  return i18n;
}